'use client'

import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdCampaign,
    MdLink,
    MdLinkOff,
    MdCheckCircle,
    MdWarning,
    MdRefresh,
    MdContentCopy,
    MdSend,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import { copyToClipboard } from '@/lib/utils/clipboard'
import {
    getMetaConnection,
    getMetaAssets,
    getMetaOAuthUrl,
    updateMetaSelection,
    disconnectMeta,
    sendMetaTestEvent,
    MetaConnectionSummary,
    MetaAssets,
} from '@/lib/api/marketing/meta-api'
import MetaMessagingSection from './meta-messaging-section'
import MetaDirectConnectionForm from './meta-direct-connection-form'

const STATUS_BADGE: Record<string, { label: string; class: string }> = {
    ACTIVE: { label: 'Conectado', class: 'badge-success' },
    PENDING: { label: 'Pendente', class: 'badge-warning' },
    EXPIRED: { label: 'Token expirado', class: 'badge-error' },
    REVOKED: { label: 'Desconectado', class: 'badge-ghost' },
}

/**
 * Snippet do Pixel para o site PRÓPRIO do tenant. É o mesmo carregamento que a
 * loja do sistema já faz sozinha (apps/shop/src/components/meta-pixel.tsx) —
 * site externo (WordPress, landing) não passa por lá e precisa do código colado.
 */
const buildPixelSnippet = (pixelId: string): string => `<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${pixelId}');
fbq('track', 'PageView');
</script>
<noscript><img height="1" width="1" style="display:none"
src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"
/></noscript>
<!-- End Meta Pixel Code -->`

export default function MetaIntegrationPage() {
    const [searchParams, setSearchParams] = useSearchParams()
    const [connection, setConnection] = useState<MetaConnectionSummary | null>(null)
    const [assets, setAssets] = useState<MetaAssets | null>(null)
    const [loading, setLoading] = useState(true)
    const [connecting, setConnecting] = useState(false)
    const [saving, setSaving] = useState(false)
    const [showDisconnect, setShowDisconnect] = useState(false)
    const [disconnecting, setDisconnecting] = useState(false)
    const [sendingTest, setSendingTest] = useState(false)
    const [testEventCode, setTestEventCode] = useState('')

    const loadAssets = useCallback(async () => {
        const res = await getMetaAssets()
        if (res.success) setAssets(res.result)
    }, [])

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const res = await getMetaConnection()
            if (res.success) {
                setConnection(res.result)
                setTestEventCode(res.result.capiTestEventCode || '')
                // Só o login na Meta dá acesso a contas e Páginas para listar
                if (res.result.status === 'ACTIVE' && res.result.authMode === 'OAUTH') {
                    await loadAssets()
                }
            }
        } finally {
            setLoading(false)
        }
    }, [loadAssets])

    useEffect(() => {
        load()
    }, [load])

    // Retorno do callback OAuth (?meta_connected=1|0)
    useEffect(() => {
        const connected = searchParams.get('meta_connected')
        if (connected === '1') {
            toast.success('Conta Meta conectada com sucesso')
            searchParams.delete('meta_connected')
            setSearchParams(searchParams, { replace: true })
        } else if (connected === '0') {
            const err = searchParams.get('error') || 'desconhecido'
            toast.error(`Falha ao conectar a conta Meta (${err})`)
            searchParams.delete('meta_connected')
            searchParams.delete('error')
            setSearchParams(searchParams, { replace: true })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleConnect = async () => {
        setConnecting(true)
        try {
            const res = await getMetaOAuthUrl()
            if (res.success && res.result?.url) {
                window.location.href = res.result.url
            } else {
                toast.error('Não foi possível iniciar a conexão com a Meta')
                setConnecting(false)
            }
        } catch {
            toast.error('Erro ao iniciar a conexão com a Meta')
            setConnecting(false)
        }
    }

    const handleSelect = async (field: 'adAccountId' | 'pixelId', value: string) => {
        setSaving(true)
        try {
            const res = await updateMetaSelection({ [field]: value || null })
            if (res.success) {
                setConnection(res.result)
                toast.success('Seleção atualizada')
                // Mudar a conta de anúncio recarrega os pixels disponíveis
                if (field === 'adAccountId') await loadAssets()
            } else {
                toast.error(res.error || 'Falha ao salvar a seleção')
            }
        } finally {
            setSaving(false)
        }
    }

    const selectedLeadAdsPages = connection?.leadAdsPageIds ?? (connection?.pageId ? [connection.pageId] : [])
    // Páginas da conexão + as já marcadas que a conexão não enxerga mais (para poder desmarcar)
    const leadAdsPageOptions = [
        ...(assets?.pages ?? []).map(p => ({ id: p.id, name: p.name, available: true })),
        ...selectedLeadAdsPages
            .filter(id => !(assets?.pages ?? []).some(p => p.id === id))
            .map(id => ({ id, name: undefined as string | undefined, available: false })),
    ]

    const handleToggleLeadAdsPage = async (pageId: string) => {
        const next = selectedLeadAdsPages.includes(pageId)
            ? selectedLeadAdsPages.filter(id => id !== pageId)
            : [...selectedLeadAdsPages, pageId]
        setSaving(true)
        try {
            const res = await updateMetaSelection({ leadAdsPageIds: next })
            if (res.success) {
                setConnection(res.result)
                toast.success(
                    next.length > selectedLeadAdsPages.length
                        ? 'Página assinada para receber leads de formulário'
                        : 'Página não recebe mais leads de formulário'
                )
            } else {
                toast.error(res.error || 'Falha ao salvar as páginas')
            }
        } finally {
            setSaving(false)
        }
    }

    const handleCopySnippet = async () => {
        if (!connection?.pixelId) return
        const copied = await copyToClipboard(buildPixelSnippet(connection.pixelId))
        if (copied) {
            toast.success('Código do Pixel copiado')
        } else {
            toast.error('Não foi possível copiar. Selecione o texto e copie manualmente.')
        }
    }

    const handleSaveTestEventCode = async () => {
        setSaving(true)
        try {
            const res = await updateMetaSelection({ capiTestEventCode: testEventCode.trim() || null })
            if (res.success) {
                setConnection(res.result)
                toast.success(res.result.capiTestEventCode ? 'Código de teste salvo' : 'Código de teste removido')
            } else {
                toast.error(res.error || 'Falha ao salvar o código de teste')
            }
        } finally {
            setSaving(false)
        }
    }

    const handleDirectSaved = (saved: MetaConnectionSummary) => {
        setAssets(null)
        setConnection(saved)
    }

    const handleSendTestEvent = async () => {
        setSendingTest(true)
        try {
            const res = await sendMetaTestEvent()
            if (!res.success) {
                toast.error(res.error || 'Falha ao enviar o evento de teste')
                return
            }
            if (res.result.ok) {
                toast.success(
                    res.result.testEventCode
                        ? `Evento recebido pela Meta. Confira em Eventos de teste (código ${res.result.testEventCode}).`
                        : 'Evento recebido pela Meta.'
                )
            } else {
                toast.error(res.result.error || 'A Meta recusou o evento de teste')
            }
        } finally {
            setSendingTest(false)
        }
    }

    const handleDisconnect = async () => {
        setDisconnecting(true)
        try {
            const res = await disconnectMeta()
            if (res.success) {
                toast.success('Conta Meta desconectada')
                setShowDisconnect(false)
                setAssets(null)
                await load()
            } else {
                toast.error('Falha ao desconectar')
            }
        } finally {
            setDisconnecting(false)
        }
    }

    const isActive = connection?.status === 'ACTIVE'
    // Conexão sem login (token do pixel ou parceiro): só eventos de conversão
    const isDirect = connection?.authMode === 'PARTNER' || connection?.authMode === 'PIXEL_TOKEN'
    const badge = connection?.status ? STATUS_BADGE[connection.status] : null

    return (
        <div className="p-4 md:p-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                    <MdCampaign className="text-2xl text-secondary" />
                </div>
                <div>
                    <h1 className="text-xl md:text-2xl font-bold text-base-content">
                        Integração Meta (Facebook/Instagram Ads)
                    </h1>
                    <p className="text-sm text-base-content/60">
                        Conecte a conta da Meta para atribuição de conversões, captação de leads e atendimento pelo
                        Instagram Direct e pelo Messenger.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            ) : !isActive ? (
                <div className="space-y-6">
                    <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-2xl border border-base-300/50 shadow-lg p-10 md:p-16 text-center">
                        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary/10 flex items-center justify-center">
                            <MdLink className="text-4xl text-secondary/50" />
                        </div>
                        <h3 className="text-lg md:text-xl font-bold text-base-content mb-2">
                            {connection?.status === 'EXPIRED' ? 'Conexão expirada' : 'Nenhuma conta Meta conectada'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-6 max-w-md mx-auto">
                            {connection?.status === 'EXPIRED'
                                ? 'O token de acesso expirou. Reconecte a conta para continuar.'
                                : 'Conecte a conta da Meta para enviar eventos de conversão (CAPI) e receber leads de formulários (Lead Ads).'}
                        </p>
                        {connection?.lastError && (
                            <p className="text-xs text-error/70 mb-4">Último erro: {connection.lastError}</p>
                        )}
                        <button className="btn btn-secondary gap-2" onClick={handleConnect} disabled={connecting}>
                            {connecting ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <MdLink className="text-lg" />
                            )}
                            {connection?.status === 'EXPIRED' ? 'Reconectar conta Meta' : 'Conectar conta Meta'}
                        </button>
                    </div>

                    <MetaDirectConnectionForm connection={connection} onSaved={handleDirectSaved} />
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Status */}
                    <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <MdCheckCircle className="text-3xl text-success" />
                                <div>
                                    <p className="font-semibold text-base-content">
                                        {isDirect ? 'Pixel conectado sem login' : 'Conta conectada'}
                                    </p>
                                    <p className="text-xs text-base-content/60">
                                        {isDirect ? (
                                            <>
                                                Pixel {connection?.pixelId} ·{' '}
                                                {connection?.authMode === 'PIXEL_TOKEN'
                                                    ? 'token do Gerenciador de Eventos'
                                                    : 'compartilhado com a plataforma'}
                                            </>
                                        ) : (
                                            <>
                                                ID Meta: {connection?.metaUserId || '-'}
                                                {connection?.tokenExpiresAt && (
                                                    <>
                                                        {' '}
                                                        · expira em{' '}
                                                        {new Date(connection.tokenExpiresAt).toLocaleDateString(
                                                            'pt-BR'
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </p>
                                </div>
                            </div>
                            {badge && <span className={`badge ${badge.class}`}>{badge.label}</span>}
                        </div>
                    </div>

                    {isDirect ? (
                        <MetaDirectConnectionForm connection={connection} onSaved={handleDirectSaved} />
                    ) : (
                        <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 space-y-5">
                            <h3 className="font-semibold text-base-content">Configuração</h3>

                            <div className="form-control w-full">
                                <label className="label py-1">
                                    <span className="label-text text-sm font-medium">Conta de anúncio</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={connection?.adAccountId || ''}
                                    disabled={saving}
                                    onChange={e => handleSelect('adAccountId', e.target.value)}
                                >
                                    <option value="">Selecione…</option>
                                    {assets?.adAccounts.map(a => (
                                        <option key={a.id} value={a.id}>
                                            {a.name || a.id}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label py-1">
                                    <span className="label-text text-sm font-medium">Pixel (Conversions API)</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={connection?.pixelId || ''}
                                    disabled={saving || !connection?.adAccountId}
                                    onChange={e => handleSelect('pixelId', e.target.value)}
                                >
                                    <option value="">Selecione…</option>
                                    {assets?.pixels.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.name || p.id}
                                        </option>
                                    ))}
                                </select>
                                {!connection?.adAccountId && (
                                    <span className="label-text-alt text-base-content/50 mt-1">
                                        Selecione a conta de anúncio primeiro.
                                    </span>
                                )}
                            </div>

                            <div className="form-control w-full">
                                <label className="label py-1">
                                    <span className="label-text text-sm font-medium">Páginas (Lead Ads)</span>
                                </label>
                                {leadAdsPageOptions.length === 0 ? (
                                    <p className="text-sm text-base-content/50 border border-dashed border-base-300 rounded-lg px-3 py-3">
                                        Nenhuma página disponível nesta conexão. Reconecte a conta da Meta autorizando as
                                        páginas.
                                    </p>
                                ) : (
                                    <div className="border border-base-300 rounded-lg divide-y divide-base-200 max-h-60 overflow-y-auto">
                                        {leadAdsPageOptions.map(page => (
                                            <label
                                                key={page.id}
                                                className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-base-200 transition-colors ${
                                                    saving ? 'opacity-60 pointer-events-none' : ''
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-secondary checkbox-sm"
                                                    checked={selectedLeadAdsPages.includes(page.id)}
                                                    disabled={saving}
                                                    onChange={() => handleToggleLeadAdsPage(page.id)}
                                                />
                                                <span className="text-sm flex-1 min-w-0 truncate">{page.name || page.id}</span>
                                                {!page.available && (
                                                    <span className="badge badge-warning badge-sm shrink-0">
                                                        sem acesso na conexão
                                                    </span>
                                                )}
                                            </label>
                                        ))}
                                    </div>
                                )}
                                <span className="label-text-alt text-base-content/50 mt-1">
                                    Cada página marcada é assinada para receber os leads dos formulários dela. Desmarcar
                                    para de receber.
                                </span>
                            </div>

                            <div className="form-control w-full">
                                <label className="label py-1" htmlFor="meta-oauth-test-code">
                                    <span className="label-text text-sm font-medium">Código de teste (opcional)</span>
                                </label>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <input
                                        id="meta-oauth-test-code"
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Ex.: TEST12345"
                                        value={testEventCode}
                                        onChange={e => setTestEventCode(e.target.value)}
                                        disabled={saving}
                                    />
                                    <button
                                        className="btn btn-outline shrink-0"
                                        onClick={handleSaveTestEventCode}
                                        disabled={
                                            saving || testEventCode.trim() === (connection?.capiTestEventCode || '')
                                        }
                                    >
                                        Salvar
                                    </button>
                                </div>
                                <span className="label-text-alt text-base-content/50 mt-1">
                                    Copie em Eventos de teste, no Gerenciador de Eventos. Com o código preenchido, todos
                                    os eventos aparecem só lá. Apague para medir de verdade.
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Evento de teste com as credenciais reais da organização */}
                    {connection?.pixelId && (
                        <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div>
                                    <h3 className="font-semibold text-base-content">Testar envio</h3>
                                    <p className="text-sm text-base-content/60 mt-1">
                                        {connection.capiTestEventCode
                                            ? `Envia uma compra de teste de R$ 1,00. Ela aparece em Eventos de teste no Gerenciador de Eventos (código ${connection.capiTestEventCode}).`
                                            : 'Sem código de teste, a compra de R$ 1,00 entra na medição real do pixel. Preencha o código de teste antes de usar.'}
                                    </p>
                                </div>
                                <button
                                    className="btn btn-outline btn-sm gap-1 shrink-0"
                                    onClick={handleSendTestEvent}
                                    disabled={sendingTest || !connection.capiTestEventCode}
                                >
                                    {sendingTest ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        <MdSend className="text-base" />
                                    )}
                                    Enviar evento de teste
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Pixel no site próprio do tenant */}
                    {connection?.pixelId && (
                        <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 space-y-4">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h3 className="font-semibold text-base-content">Pixel no seu site</h3>
                                    <p className="text-sm text-base-content/60 mt-1">
                                        A loja do sistema já carrega o Pixel sozinha. No site próprio da empresa, cole o
                                        código abaixo dentro da tag{' '}
                                        <code className="px-1 py-0.5 rounded bg-base-200 text-xs">&lt;head&gt;</code> de
                                        todas as páginas.
                                    </p>
                                </div>
                                <button className="btn btn-sm btn-outline gap-1 shrink-0" onClick={handleCopySnippet}>
                                    <MdContentCopy className="text-base" /> Copiar
                                </button>
                            </div>

                            <pre className="bg-base-200 rounded-xl p-4 text-xs leading-relaxed overflow-x-auto text-base-content/80">
                                <code>{buildPixelSnippet(connection.pixelId)}</code>
                            </pre>

                            <p className="text-xs text-base-content/50">
                                Sem o Pixel no site, só são medidos os eventos enviados pelo servidor (CAPI): perde-se a
                                navegação e a deduplicação entre navegador e servidor.
                            </p>
                        </div>
                    )}

                    {/* Instagram Direct e Messenger dependem do login na Meta */}
                    {!isDirect && <MetaMessagingSection />}

                    {/* Ações */}
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <button className="btn btn-ghost btn-sm gap-1" onClick={load} disabled={saving}>
                                <MdRefresh className="text-lg" /> Atualizar
                            </button>
                            {/* Reabre o login da Meta sem desconectar: é como se autorizam páginas,
                                contas e permissões novas. A reconexão mantém pixel, páginas e código de teste. */}
                            {!isDirect && (
                                <button
                                    className="btn btn-ghost btn-sm gap-1"
                                    onClick={handleConnect}
                                    disabled={connecting || saving}
                                    title="Abrir o login da Meta para autorizar páginas, contas e permissões novas"
                                >
                                    {connecting ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        <MdLink className="text-lg" />
                                    )}
                                    Reautorizar
                                </button>
                            )}
                        </div>
                        <button
                            className="btn btn-ghost btn-sm text-error gap-1"
                            onClick={() => setShowDisconnect(true)}
                        >
                            <MdLinkOff className="text-lg" /> Desconectar
                        </button>
                    </div>

                    {connection?.lastError && (
                        <div className="alert alert-warning">
                            <MdWarning />
                            <span className="text-sm">{connection.lastError}</span>
                        </div>
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={showDisconnect}
                onClose={() => setShowDisconnect(false)}
                onConfirm={handleDisconnect}
                title="Desconectar conta Meta"
                message="A integração será removida. Os eventos de conversão (CAPI) e a captação de Lead Ads deixarão de funcionar até reconectar. Deseja continuar?"
                confirmButtonText="Desconectar"
                cancelButtonText="Cancelar"
                loading={disconnecting}
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
